import {
  applyStreamAspectToElement,
  normalizeStreamSize,
  type StreamVideoSize,
} from './streamAspectSize';
import {
  getStreamCustomerCode,
  isStreamPlayerHost,
  readMwAspectFromSrc,
  readUidFromStreamPlayerSrc,
} from './streamVideo';

export {
  applyStreamAspectToElement,
  cssAspectFromSize,
  gcd,
  MAX_STREAM_ASPECT_DIMENSION,
  MAX_STREAM_ASPECT_RATIO,
  MIN_STREAM_ASPECT_DIMENSION,
  normalizeStreamSize,
  orientationFromSize,
  reduceStreamRatio,
} from './streamAspectSize';
export type { StreamVideoOrientation, StreamVideoSize } from './streamAspectSize';

export const STREAM_ASPECT_FETCH_TIMEOUT_MS = 2500;
export const STREAM_ASPECT_FETCH_CONCURRENCY = 4;

type CachedStreamSize =
  | { ok: true; size: StreamVideoSize }
  | { ok: false };

const sizeCache = new Map<string, CachedStreamSize>();
const inflight = new Map<string, Promise<StreamVideoSize | null>>();
const waiters: Array<() => void> = [];
let activeFetches = 0;

export function resetStreamAspectCache() {
  sizeCache.clear();
  inflight.clear();
  waiters.splice(0, waiters.length);
  activeFetches = 0;
}

export function peekCachedStreamPlayerSize(src: string): StreamVideoSize | null {
  const fromSrc = readMwAspectFromSrc(src);
  if (fromSrc) return fromSrc;
  const uid = readUidFromStreamPlayerSrc(src);
  if (!uid) return null;
  const cached = sizeCache.get(uid);
  return cached?.ok ? cached.size : null;
}

export function readStreamSizeFromUnknown(data: unknown): StreamVideoSize | null {
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  const dimensions =
    record.dimensions && typeof record.dimensions === 'object'
      ? (record.dimensions as Record<string, unknown>)
      : null;
  const input =
    record.input && typeof record.input === 'object'
      ? (record.input as Record<string, unknown>)
      : null;
  const width = Number(dimensions?.width ?? input?.width ?? record.width);
  const height = Number(dimensions?.height ?? input?.height ?? record.height);
  return normalizeStreamSize(width, height);
}

export function readStreamSizeFromManifest(manifest: string): StreamVideoSize | null {
  let best: StreamVideoSize | null = null;
  const pattern = /RESOLUTION=(\d+)x(\d+)/gi;
  for (let match = pattern.exec(manifest); match; match = pattern.exec(manifest)) {
    const size = normalizeStreamSize(Number(match[1]), Number(match[2]));
    if (!size) continue;
    if (!best || size.width * size.height > best.width * best.height) {
      best = size;
    }
  }
  return best;
}

function streamMediaBase(uid: string) {
  const code = getStreamCustomerCode();
  const safeUid = encodeURIComponent(uid);
  return code
    ? `https://customer-${code}.cloudflarestream.com/${safeUid}`
    : `https://videodelivery.net/${safeUid}`;
}

export function buildStreamPlayerMetadataUrl(uid: string) {
  return `${streamMediaBase(uid)}/metadata/playerEnhancementInfo.json`;
}

export function buildStreamManifestUrl(uid: string) {
  return `${streamMediaBase(uid)}/manifest/video.m3u8`;
}

function isBlockedNetworkError(error: unknown) {
  return error instanceof TypeError;
}

function fetchStreamPublic(url: string, timeoutMs = STREAM_ASPECT_FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, {
    signal: controller.signal,
    credentials: 'omit',
    mode: 'cors',
  }).finally(() => window.clearTimeout(timer));
}

async function fetchJsonSize(url: string): Promise<StreamVideoSize | null> {
  const response = await fetchStreamPublic(url);
  if (!response.ok) return null;
  return readStreamSizeFromUnknown(await response.json().catch(() => null));
}

async function fetchManifestSize(url: string): Promise<StreamVideoSize | null> {
  const response = await fetchStreamPublic(url);
  if (!response.ok) return null;
  const text = await response.text().catch(() => '');
  return readStreamSizeFromManifest(text);
}

async function withAspectFetchSlot<T>(fn: () => Promise<T>): Promise<T> {
  if (activeFetches >= STREAM_ASPECT_FETCH_CONCURRENCY) {
    await new Promise<void>((resolve) => {
      waiters.push(resolve);
    });
  }
  activeFetches += 1;
  try {
    return await fn();
  } finally {
    activeFetches -= 1;
    waiters.shift()?.();
  }
}

async function fetchStreamPlayerSize(uid: string): Promise<StreamVideoSize | null> {
  return withAspectFetchSlot(async () => {
    try {
      const fromMeta = await fetchJsonSize(buildStreamPlayerMetadataUrl(uid));
      if (fromMeta) return fromMeta;
    } catch (error) {
      // CORS / network blocks log once in the browser; skip HLS so we do not
      // double the console error for the same public host.
      if (isBlockedNetworkError(error)) return null;
    }
    try {
      return await fetchManifestSize(buildStreamManifestUrl(uid));
    } catch {
      return null;
    }
  });
}

export async function resolveStreamPlayerSize(
  src: string,
  signal?: AbortSignal
): Promise<StreamVideoSize | null> {
  if (!isStreamPlayerHost(src)) return null;
  const fromSrc = readMwAspectFromSrc(src);
  if (fromSrc) return fromSrc;
  const uid = readUidFromStreamPlayerSrc(src);
  if (!uid) return null;

  const cached = sizeCache.get(uid);
  if (cached) return cached.ok ? cached.size : null;
  if (signal?.aborted) return null;

  let pending = inflight.get(uid);
  if (!pending) {
    pending = fetchStreamPlayerSize(uid)
      .then((size) => {
        sizeCache.set(uid, size ? { ok: true, size } : { ok: false });
        return size;
      })
      .catch(() => {
        sizeCache.set(uid, { ok: false });
        return null;
      })
      .finally(() => {
        inflight.delete(uid);
      });
    inflight.set(uid, pending);
  }

  const size = await pending;
  if (signal?.aborted) return null;
  return size;
}

export function applyKnownStreamAspects(root: ParentNode) {
  Array.from(root.querySelectorAll('iframe.mw-stream-video')).forEach((node) => {
    const iframe = node as HTMLIFrameElement;
    const src = iframe.getAttribute('src') || '';
    if (!isStreamPlayerHost(src)) return;
    const size = peekCachedStreamPlayerSize(src);
    if (size) applyStreamAspectToElement(iframe, size.width, size.height);
  });
  Array.from(root.querySelectorAll('video.mw-stream-video')).forEach((node) => {
    const video = node as HTMLVideoElement;
    const apply = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        applyStreamAspectToElement(video, video.videoWidth, video.videoHeight);
      }
    };
    if (video.readyState >= 1) {
      apply();
      return;
    }
    video.addEventListener('loadedmetadata', apply, { once: true });
  });
}

export function applyStreamPlayerAspects(root: ParentNode): () => void {
  applyKnownStreamAspects(root);
  const controller = new AbortController();
  Array.from(root.querySelectorAll('iframe.mw-stream-video')).forEach((node) => {
    const iframe = node as HTMLIFrameElement;
    if (iframe.dataset.orientation) return;
    const src = iframe.getAttribute('src') || '';
    if (!isStreamPlayerHost(src)) return;
    void resolveStreamPlayerSize(src, controller.signal)
      .then((size) => {
        if (!size || controller.signal.aborted) return;
        applyStreamAspectToElement(iframe, size.width, size.height);
      })
      .catch(() => undefined);
  });
  return () => controller.abort();
}
